import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { track } from "./gtag";
import { metaTrack } from "./meta";
import { resolveTierPrice } from "./pricing";

const CartContext = createContext(null);
const STORAGE_KEY = "pg_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  /** Returns the unit price for a line item at a given qty, honouring
   *  any saved tier ladder on the line. `on_sale` is passed through so
   *  the sale-guard in resolveTierPrice actually fires — otherwise an
   *  on-sale SKU still gets a stacked bulk-tier discount in the basket
   *  (server rejects/recomputes at checkout, so the customer would see
   *  the "prices updated" toast). */
  const lineUnitPrice = (line, qty) => {
    if (line?.price_tiers?.length) {
      return resolveTierPrice(
        {
          price: line.base_price ?? line.price,
          price_tiers: line.price_tiers,
          on_sale: !!line.on_sale,
        },
        qty,
      );
    }
    return Number(line?.price || 0);
  };

  /** Server-side order pricing gives Laboratory Reagents their bundle_price
   * whenever the order also contains a peptide (see create_order). This
   * mirrors that rule so the basket never disagrees with what the server
   * will charge — reagent lines flip to bundle price when a peptide is
   * present and back to list price when the last peptide is removed. */
  const syncBundlePrices = (lines) => {
    const hasPeptide = lines.some((l) => (l.category || "Research Peptide") !== "Laboratory Reagents");
    return lines.map((l) => {
      if ((l.category || "") !== "Laboratory Reagents" || !(Number(l.bundle_price) > 0)) return l;
      const unit = hasPeptide ? Number(l.bundle_price) : Number(l.base_price ?? l.price);
      if (Math.abs(unit - Number(l.price || 0)) > 0.005) return { ...l, price: unit };
      return l;
    });
  };

  const addItem = (product, quantity = 1) => {
    track.addToCart(product, quantity);
    metaTrack.addToCart(product, quantity);
    setItems((prev) => {
      const existing = prev.find((i) => i.sku === product.sku);
      if (existing) {
        const newQty = existing.quantity + quantity;
        return syncBundlePrices(prev.map((i) =>
          i.sku === product.sku
            ? { ...i, quantity: newQty, price: lineUnitPrice(i, newQty) }
            : i,
        ));
      }
      const tiers = product.price_tiers || null;
      const basePrice = Number(product.price);
      const onSale = !!product.on_sale;
      const unit = tiers
        ? resolveTierPrice({ price: basePrice, price_tiers: tiers, on_sale: onSale }, quantity)
        : basePrice;
      return syncBundlePrices([
        ...prev,
        {
          product_id: product.id,
          sku: product.sku,
          name: product.name,
          strength: product.strength,
          price: unit,
          base_price: basePrice,
          price_tiers: tiers,
          on_sale: onSale,
          category: product.category || null,
          bundle_price: Number(product.bundle_price) > 0 ? Number(product.bundle_price) : null,
          quantity,
        },
      ]);
    });
  };

  const updateQty = (sku, quantity) => {
    setItems((prev) =>
      syncBundlePrices(prev
        .map((i) =>
          i.sku === sku
            ? { ...i, quantity: Math.max(0, quantity), price: lineUnitPrice(i, Math.max(0, quantity)) }
            : i,
        )
        .filter((i) => i.quantity > 0)),
    );
  };

  const removeItem = (sku) => {
    setItems((prev) => syncBundlePrices(prev.filter((i) => i.sku !== sku)));
  };

  const clear = () => setItems([]);

  /** Pull authoritative pricing from a fresh `/api/products` response
   * and re-sync each line item. Catalogue prices and tier ladders are
   * the only source of truth — the localStorage cart can be days old
   * (or weeks, on a returning visitor), so a stale £3.99 line item
   * shouldn't be allowed to render an £8.98 cart total when the
   * server will charge £9.98 on order create.
   *
   * Returns the array of SKUs whose unit price actually changed so the
   * caller can surface a "prices updated" notice.
   */
  const refreshPrices = (serverProducts) => {
    const bySku = {};
    for (const p of serverProducts || []) bySku[p.sku] = p;

    const changed = [];
    setItems((prev) => {
      // Bundle rule mirrors the server: reagents get bundle_price only when
      // the basket also holds at least one non-reagent line.
      const hasPeptide = prev.some((l) => {
        const cat = (bySku[l.sku]?.category ?? l.category) || "Research Peptide";
        return cat !== "Laboratory Reagents";
      });
      return prev.map((line) => {
        const fresh = bySku[line.sku];
        if (!fresh) return line; // product removed/hidden — leave as-is, server will reject on order create
        const freshBase = Number(fresh.price);
        const freshTiers = fresh.price_tiers || null;
        const freshOnSale = !!fresh.on_sale;
        const freshCategory = fresh.category || null;
        const freshBundle = Number(fresh.bundle_price) > 0 ? Number(fresh.bundle_price) : null;
        let freshUnit = freshTiers
          ? resolveTierPrice({ price: freshBase, price_tiers: freshTiers, on_sale: freshOnSale }, line.quantity)
          : freshBase;
        if (freshCategory === "Laboratory Reagents" && freshBundle && hasPeptide) {
          freshUnit = freshBundle;
        }
        if (Math.abs(freshUnit - Number(line.price || 0)) > 0.005 ||
            Math.abs(freshBase - Number(line.base_price || 0)) > 0.005) {
          changed.push({ sku: line.sku, name: line.name, was: line.price, now: freshUnit });
          return { ...line, price: freshUnit, base_price: freshBase, price_tiers: freshTiers, on_sale: freshOnSale, category: freshCategory, bundle_price: freshBundle };
        }
        return { ...line, category: freshCategory, bundle_price: freshBundle };
      });
    });
    return changed;
  };

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  // Memoise so cart-aware components (header counter, mini-cart, checkout
  // page) only re-render when items actually change. Without this every
  // unrelated parent re-render allocates a new value object and forces a
  // cascade of consumer re-renders.
  const value = useMemo(
    () => ({ items, addItem, updateQty, removeItem, clear, refreshPrices, subtotal, count }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, subtotal, count],
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
